import { useState, useEffect, useMemo } from 'react';
import { useSugarReadings7Days } from '../hooks/useSugarReadings';

interface GlucoseReading {
  reading_value: number;
  reading_time: string;
}

interface DayData {
  date: string;
  label: string;
  readings: GlucoseReading[];
  color: string;
  enabled: boolean;
}

function getValue(r: any): number {
  return Number(r?.value ?? r?.reading_value ?? r?.reading ?? 0);
}

function getTime(r: any): string {
  return r?.readingTime ?? r?.reading_time ?? r?.createdAt ?? '';
}

export function MultiDayGlucoseChart() {
  const colors = ['#72a83f', '#72a83f', '#72a83f', '#72a83f', '#84cc16', '#72a83f', '#ef4444'];

  const _r = useSugarReadings7Days();
  const result = _r != null && typeof _r === 'object' ? _r : { data: undefined, isLoading: true };
  const allReadingsData = result.data;
  const isLoading = result?.isLoading ?? false;

  const allReadings = Array.isArray(allReadingsData)
    ? allReadingsData
    : allReadingsData?.data || allReadingsData || [];

  const dayData = useMemo(() => {
    const result: DayData[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayStart = `${dateStr}T00:00:00`;
      const dayEnd = `${dateStr}T23:59:59`;

      const dayReadings = allReadings.filter((r) => {
        const t = getTime(r);
        if (!t) return false;
        return t >= dayStart && t <= dayEnd;
      });

      result.push({
        date: dateStr,
        label: `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`,
        readings: dayReadings.map((r) => ({
          reading_value: getValue(r),
          reading_time: getTime(r),
        })),
        color: colors[6 - i],
        enabled: true,
      });
    }
    return result;
  }, [allReadings]);

  const [dayDataState, setDayDataState] = useState<DayData[]>([]);

  useEffect(() => {
    setDayDataState(dayData);
  }, [dayData]);

  const toggleDay = (index: number) => {
    setDayDataState((prev) =>
      prev.map((day, i) => (i === index ? { ...day, enabled: !day.enabled } : day))
    );
  };

  const toggleAll = () => {
    const allEnabled = dayDataState.every((d) => d.enabled);
    setDayDataState((prev) => prev.map((day) => ({ ...day, enabled: !allEnabled })));
  };

  const getYPosition = (value: number) => {
    const min = 0;
    const max = 360;
    const chartHeight = 300;
    return chartHeight - ((value - min) / (max - min)) * chartHeight;
  };

  const getXPosition = (time: string) => {
    const d = new Date(time);
    const hour = d.getHours() + d.getMinutes() / 60;
    return (hour / 24) * 600;
  };

  const displayData = dayDataState.length > 0 ? dayDataState : dayData;

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-dropsugar-blue/10 to-dropsugar-green/10 rounded-2xl p-6 border-2 border-dropsugar-blue/20 animate-pulse">
        <div className="h-8 bg-white rounded w-1/3 mb-4"></div>
        <div className="h-64 bg-white rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-dropsugar-blue/10 to-dropsugar-green/10 rounded-2xl p-6 border-2 border-dropsugar-blue/20">
      <h3 className="text-xl font-bold mb-6 text-gray-900">Glucose Trend</h3>

      <div className="bg-white rounded-xl p-6 mb-4 overflow-x-auto border-2 border-dropsugar-green/30">
        <div className="relative" style={{ height: '320px', minWidth: '600px' }}>
          <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-600 pr-2">
            <span>360</span>
            <span>288</span>
            <span>216</span>
            <span>144</span>
            <span>72</span>
            <span>0</span>
          </div>

          <div className="absolute left-12 right-0 top-0 bottom-0">
            <div className="absolute inset-0 flex items-end">
              <div className="w-full h-full relative bg-gradient-to-b from-dropsugar-blue/10 to-transparent">
                <div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-dropsugar-green/10 to-transparent"
                  style={{ top: '50%', height: '25%' }}
                ></div>
              </div>
            </div>

            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 600 300"
              preserveAspectRatio="none"
            >
              {displayData
                .filter((day) => day.enabled && day.readings.length > 0)
                .map((day) => {
                  const points = day.readings
                    .map((reading) => {
                      const x = getXPosition(reading.reading_time);
                      const y = getYPosition(reading.reading_value);
                      return `${x},${y}`;
                    })
                    .join(' ');

                  return (
                    <polyline
                      key={day.date}
                      points={points}
                      fill="none"
                      stroke={day.color}
                      strokeWidth="2"
                      strokeLinejoin="round"
                      opacity="0.9"
                    />
                  );
                })}
            </svg>
          </div>

          <div className="absolute left-12 right-0 bottom-0 flex justify-between text-xs text-gray-600 pt-2">
            <span>02:00</span>
            <span>06:00</span>
            <span>10:00</span>
            <span>14:00</span>
            <span>18:00</span>
            <span>22:00</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-700">Select Date</span>
        <button
          onClick={toggleAll}
          className="px-4 py-2 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-full text-sm font-medium transition-colors"
        >
          Select All
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {displayData.map((day, index) => (
          <button
            key={day.date}
            onClick={() => toggleDay(index)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all border-2 ${
              day.enabled
                ? 'bg-white border-dropsugar-green/50 text-gray-900'
                : 'bg-gray-100 border-gray-300 text-gray-500'
            }`}
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: day.enabled ? day.color : '#d1d5db' }}
            ></div>
            <span className="text-sm font-medium">{day.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
